import { ref } from 'vue';
import type { Ref } from 'vue';

export enum ValueSelectorType {
  Assignee = 'assignee',
  Boolean = 'boolean',
  BusinessUnit = 'businessUnit',
  Country = 'country',
  Currency = 'currency',
  Customer = 'customer',
  Date = 'date',
  DunningState = 'dunningState',
  Enum = 'enum',
  InvoiceStatus = 'status',
  Monetary = 'monetary',
  Numeric = 'numeric',
  PartnerInformation = 'partnerInformation',
  Segment = 'segment',
  Text = 'text',
}

export interface ValueSelector {
  // Format the value for display in the UI
  readonly formatValue: (value: any, field: Field) => string;

  readonly props?: Record<string, any>;

  readonly toJsonQueryValue: (value: any, field: Field) => any;

  readonly type: ValueSelectorType;
}

export interface Comparator {
  // The comparator value used in the backend
  readonly key: string;

  // UI-friendly label
  readonly label: string;

  // Optional label for plural values
  readonly pluralLabel?: string;
}

export interface Predicate {
  readonly comparator: Comparator;

  readonly label?: string;

  readonly pluralLabel?: string;

  readonly value?: string | boolean;
}

export interface Field {
  readonly customBadgeLabel?: string;

  readonly flat?: boolean;

  readonly hidden?: boolean;

  readonly icon?: string;

  readonly label: string;

  // When true, uses legacy nested JSON format (e.g., { assignee: { in: [...] } })
  // Default (false/undefined) uses flat double-underscore keys (e.g., 'assignee_id__in')
  readonly legacyFormat?: boolean;

  readonly path: string[];

  readonly predicates: Predicate[];

  readonly valueSelector: ValueSelector;
}

export interface Filter {
  field: Field;

  predicate: Predicate;

  value: any;
}

export interface FilterState {
  addFilter: (field: Field, predicate: Predicate, value: any) => void;
  fields: Ref<Field[]>;

  filters: Ref<Filter[]>;
  getFilterIdxByFieldLabel: (fieldLabel: string) => number;
  removeFilter: (filterIdx: number) => void;
  resetFilterState: (initialFields: Field[]) => void;
  updateFilter: (filterIdx: number, updateData: Partial<Filter>) => void;
}

// Managed filter state
export function useFilterState(): FilterState {
  const fields = ref<Field[]>([]);
  const filters = ref<Filter[]>([]);

  const resetFilterState = (initialFields: Field[]) => {
    fields.value = initialFields;
    filters.value = [];
  };

  const getFilterIdxByFieldLabel = (fieldLabel: string): number => {
    return filters.value.findIndex((filter) => filter.field?.label === fieldLabel);
  };

  const addFilter = (field: Field, predicate: Predicate, value: any) => {
    // For now, we only allow one filter per field
    const existingFilterIdx = getFilterIdxByFieldLabel(field.label);
    if (existingFilterIdx !== -1) {
      Object.assign(filters.value[existingFilterIdx], { predicate, value });
    } else {
      filters.value.push({
        field,
        predicate,
        value,
      });
    }
  };

  const updateFilter = (filterIdx: number, updateData: Partial<Filter>) => {
    filters.value[filterIdx] = {
      ...filters.value[filterIdx],
      ...updateData,
    };
  };

  const removeFilter = (filterIdx: number) => {
    filters.value.splice(filterIdx, 1);
  };

  return {
    addFilter,
    fields,
    filters,
    getFilterIdxByFieldLabel,
    removeFilter,
    resetFilterState,
    updateFilter,
  };
}
